import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './AirportTicker.css';

const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

const AIRPORT_ORDER = ['JFK', 'LGA-C'];

const AIRPORT_LABELS = {
  'JFK': 'JFK',
  'LGA-C': 'LGA-C',
};

function parseTimeToHour(timeStr) {
  const parts = timeStr.trim().split(' ');
  let hour = parseInt(parts[0], 10);
  const period = parts[1];
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  return hour;
}

function getCurrentCategory(airport) {
  const currentHour = new Date().getHours();

  const ranges = [
    { range: airport.timeRange1, category: airport.category1 },
    { range: airport.timeRange2, category: airport.category2 },
    { range: airport.timeRange3, category: airport.category3 },
  ];

  for (const { range, category } of ranges) {
    if (!range) continue;
    const [startStr, endStr] = range.split(' - ');
    const startHour = parseTimeToHour(startStr);
    const endHour = parseTimeToHour(endStr);

    if (startHour > endHour) {
      if (currentHour >= startHour || currentHour < endHour) return category;
    } else {
      if (currentHour >= startHour && currentHour < endHour) return category;
    }
  }

  return null;
}

function getDemandClass(category) {
  if (!category) return 'demand-none';
  switch (category.toLowerCase()) {
    case 'low': return 'demand-low';
    case 'moderate': return 'demand-moderate';
    case 'high': return 'demand-high';
    default: return 'demand-none';
  }
}

function formatWaitTime(minutes) {
  if (!minutes || minutes === 0) return '--';
  if (minutes < 60) return `${minutes}m`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
}

function buildMarqueeText(airport) {
  const category = getCurrentCategory(airport);
  const label = AIRPORT_LABELS[airport.airportCode] || airport.airportCode;
  const wait = formatWaitTime(airport.avgWaitTimes);
  const lot = `${airport.lotPercentage}%`;
  const open = airport.spacesOpen;
  const dispatched = airport.disptachedTaxi;
  const demand = category || 'N/A';

  return { label, wait, lot, open, dispatched, demand, demandClass: getDemandClass(category) };
}

const AirportTicker = () => {
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState(
    () => sessionStorage.getItem('airportTickerCollapsed') === 'true'
  );
  const [stale, setStale] = useState(false);

  const fetchData = useCallback(async (signal) => {
    try {
      const { data } = await api.get('/airport-dispatch', { signal });
      const sorted = AIRPORT_ORDER
        .map(code => data.airports.find(a => a.airportCode === code))
        .filter(Boolean);
      setAirports(sorted);
      setStale(data.stale);
      setError('');
    } catch (err) {
      if (err.name === 'AbortError' || err.message === 'Request was cancelled') return;
      setError('Airport dispatch data unavailable');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);

    const intervalId = setInterval(() => {
      fetchData();
    }, REFRESH_INTERVAL_MS);

    return () => {
      controller.abort();
      clearInterval(intervalId);
    };
  }, [fetchData]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    sessionStorage.setItem('airportTickerCollapsed', String(next));
  };

  const renderMarqueeContent = () => {
    if (loading) return <span className="marquee-status">Loading dispatch data...</span>;
    if (error) return <span className="marquee-status marquee-error">{error}</span>;
    if (airports.length === 0) return <span className="marquee-status">No dispatch data</span>;

    return airports.map((airport, i) => {
      const d = buildMarqueeText(airport);
      return (
        <span key={airport.airportCode} className="marquee-item">
          {i > 0 && <span className="marquee-sep" aria-hidden="true" />}
          <span className="marquee-airport">{d.label}</span>
          <span className={`marquee-demand ${d.demandClass}`}>{d.demand}</span>
          <span className="marquee-stat">Wait <strong>{d.wait}</strong></span>
          <span className="marquee-dot" aria-hidden="true" />
          <span className="marquee-stat">Lot <strong>{d.lot}</strong></span>
          <span className="marquee-dot" aria-hidden="true" />
          <span className="marquee-stat">Open <strong>{d.open}</strong></span>
          <span className="marquee-dot" aria-hidden="true" />
          <span className="marquee-stat">Dispatched <strong>{d.dispatched}</strong></span>
        </span>
      );
    });
  };

  return (
    <div className={`airport-ticker ${collapsed ? 'collapsed' : ''}`}>
      <button className="airport-ticker-toggle" onClick={toggleCollapsed} aria-expanded={!collapsed} aria-label="Toggle airport dispatch">
        <span className="airport-ticker-icon" role="img" aria-label="airplane">&#9992;</span>
        <svg
          className={`airport-ticker-chevron ${collapsed ? 'collapsed' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>

      {!collapsed && (
        <div className="marquee-wrapper">
          <div className="marquee-track">
            <div className="marquee-content" aria-live="polite">
              {renderMarqueeContent()}
            </div>
            {/* Duplicate for seamless loop */}
            {!loading && !error && airports.length > 0 && (
              <div className="marquee-content" aria-hidden="true">
                {renderMarqueeContent()}
              </div>
            )}
          </div>
        </div>
      )}

      {stale && !collapsed && <span className="ticker-stale-badge">Delayed</span>}
    </div>
  );
};

export default AirportTicker;
